import { AxiosError, AxiosInstance, HttpStatusCode, isAxiosError } from 'axios'

import { AxiosInstanceBuilder } from '../utils/axios'
import { Constants } from '../utils/constants'
import { getHandleableError } from '../utils/errors/isXiaError'
import { FetchError } from './fetchError'
import {
  FetchBackgroundTaskStatusResponse,
  FetchResponse,
} from './fetchResponse'
import { PatchRequest } from './models'

// Singleton instance and handler
let axiosInstance: AxiosInstance | null = null
let maintenanceHandlerRef: ((value: boolean) => void) | null = null

export const instanceWithoutAuth = (): AxiosInstance => {
  // If no instance exists yet, we create a new one
  if (!axiosInstance) {
    const builder = new AxiosInstanceBuilder().withBaseUrl()
    axiosInstance = builder.build()
  }

  return axiosInstance
}

export const defaultInstance = (
  maintenanceHandler?: (value: boolean) => void,
): AxiosInstance => {
  // If a new handler is passed, we save it
  if (maintenanceHandler) {
    maintenanceHandlerRef = maintenanceHandler
  }

  // If no instance exists yet, we create a new one
  if (!axiosInstance) {
    const builder = new AxiosInstanceBuilder()
      .withBaseUrl()
      .withAuthentication()
      .withCorrelationId()
      .withTenantHeader()
      .withProjectHeader()

    // If we have a handler, we add it
    if (maintenanceHandlerRef) {
      builder.withMaintenanceHandler(maintenanceHandlerRef)
    }

    axiosInstance = builder.build()
  }

  return axiosInstance
}

// Cleanup function for the provider
export const cleanup = () => {
  axiosInstance = null
  maintenanceHandlerRef = null
}

export async function fetchWithoutAuth<T>(
  url: string,
  fallback: boolean = false,
  customHeaders?: any,
  axiosInstance: AxiosInstance = instanceWithoutAuth(),
): Promise<FetchResponse<T>> {
  try {
    const response = await axiosInstance.get<T>(url, { headers: customHeaders })
    return {
      body: response.data,
      etag: response.headers[Constants.HttpHeaderETag] || undefined,
    }
  } catch (error) {
    return handleError(error, fallback)
  }
}

export async function fetchJson<T>(
  url: string,
  fallback: boolean = false,
  customHeaders?: any,
  axiosInstance: AxiosInstance = defaultInstance(),
): Promise<FetchResponse<T>> {
  try {
    const response = await axiosInstance.get<T>(url, { headers: customHeaders })

    return {
      body: response.data,
      etag: response.headers[Constants.HttpHeaderETag] || undefined,
    }
  } catch (error) {
    return handleError(error, fallback)
  }
}

export async function fetchBackgroundTaskStatus<T>(
  url: string,
  fallback: boolean = false,
  customHeaders?: any,
  axiosInstance: AxiosInstance = defaultInstance(),
): Promise<FetchBackgroundTaskStatusResponse<T>> {
  try {
    const response = await axiosInstance.get<T>(url, {
      headers: { customHeaders },
    })

    return {
      body: response.data,
      etag: response.headers[Constants.HttpHeaderETag] || undefined,
      retry: response.headers[Constants.RetryAfter] || undefined,
    }
  } catch (error) {
    if (isAxiosError(error)) {
      const { response } = error
      if (response?.status! < 400)
        return {
          body: response?.data,
          etag: response?.headers[Constants.HttpHeaderETag] || undefined,
          retry: response?.headers[Constants.RetryAfter] || undefined,
        }
    }
    return handleError(error, fallback)
  }
}

export async function fetchFile(
  url: string,
  customHeaders?: any,
  axiosInstance: AxiosInstance = defaultInstance(),
) {
  try {
    const response = await axiosInstance.get(url, {
      responseType: 'blob',
      headers: customHeaders,
    })
    return response.data
  } catch (error) {
    return handleError(error)
  }
}

export async function fetchBlob(
  url: string,
  customHeaders?: any,
  axiosInstance: AxiosInstance = defaultInstance(),
): Promise<Blob> {
  try {
    const response = await axiosInstance.get(url, {
      responseType: 'blob',
      headers: customHeaders,
    })
    const blob = new Blob([response.data], {
      type: response.headers['content-type'],
    })

    return blob
  } catch (error) {
    return handleError<Blob>(error)
  }
}

export async function postJson<T>(
  url: string,
  body: T,
  customHeaders?: any,
  axiosInstance: AxiosInstance = defaultInstance(),
) {
  try {
    const response = await axiosInstance.post(url, body, {
      headers: customHeaders,
    })
    return response.data
  } catch (error) {
    return handleError(error)
  }
}

export async function postJsonWithHeaders<T>(
  url: string,
  body: T,
  customHeaders?: any,
  axiosInstance: AxiosInstance = defaultInstance(),
) {
  try {
    // Do not set Content-Type headers in FormData
    const headers =
      body instanceof FormData ? customHeaders : { ...customHeaders }

    const response = await axiosInstance.post(url, body, {
      headers: headers,
    })
    return {
      locationUrl: response.headers[Constants.LocationUrl] || undefined,
      retry: response.headers[Constants.RetryAfter] || undefined,
      taskId: response.headers['taskid'] || undefined,
    }
  } catch (error) {
    return handleError(error)
  }
}

export async function postJsonForBatch<T>(
  url: string,
  body: T,
  customHeaders?: any,
  axiosInstance: AxiosInstance = defaultInstance(),
) {
  try {
    const response = await axiosInstance.post(url, body, {
      headers: customHeaders,
    })
    return {
      locationUrl: response.headers[Constants.LocationUrl] || undefined,
      retry: response.headers[Constants.RetryAfter] || undefined,
    }
  } catch (error) {
    return handleError(error)
  }
}

export async function putJsonForBatch<T>(
  url: string,
  body: T,
  customHeaders?: any,
  axiosInstance: AxiosInstance = defaultInstance(),
) {
  try {
    const response = await axiosInstance.put(url, body, {
      headers: customHeaders,
    })
    return {
      locationUrl: response.headers[Constants.LocationUrl] || undefined,
      retry: response.headers[Constants.RetryAfter] || undefined,
    }
  } catch (error) {
    return handleError(error)
  }
}

export async function postForm(
  url: string,
  body: any,
  customHeaders?: any,
  axiosInstance: AxiosInstance = defaultInstance(),
) {
  try {
    const response = await axiosInstance.postForm(url, body, {
      headers: customHeaders,
    })
    return response.data
  } catch (error) {
    return handleError(error)
  }
}

export async function putJson<T>(
  url: string,
  body: T,
  customHeaders?: any,
  axiosInstance: AxiosInstance = defaultInstance(),
) {
  try {
    const response = await axiosInstance.put(url, body, {
      headers: customHeaders,
    })
    return response.data
  } catch (error) {
    return handleError(error)
  }
}

export async function putForm(
  url: string,
  body: any,
  customHeaders?: any,
  axiosInstance: AxiosInstance = defaultInstance(),
) {
  try {
    const response = await axiosInstance.putForm(url, body, {
      headers: customHeaders,
    })
    return response.data
  } catch (error) {
    return handleError(error)
  }
}

export async function patchJson<T extends object>(
  url: string,
  body?: PatchRequest<T>,
  customHeaders?: any,
  axiosInstance: AxiosInstance = defaultInstance(),
) {
  try {
    const response = await axiosInstance.patch(url, body, {
      headers: customHeaders,
    })
    return response.data
  } catch (error) {
    return handleError(error)
  }
}

export async function patchForm(
  url: string,
  body: any,
  customHeaders?: any,
  axiosInstance: AxiosInstance = defaultInstance(),
) {
  try {
    const response = await axiosInstance.patchForm(url, body, {
      headers: customHeaders,
    })
    return response.data
  } catch (error) {
    return handleError(error)
  }
}

export async function requestDelete(
  url: string,
  customHeaders?: any,
  axiosInstance: AxiosInstance = defaultInstance(),
) {
  try {
    const response = await axiosInstance.delete(url, { headers: customHeaders })
    return response.data
  } catch (error) {
    return handleError(error)
  }
}

export async function requestDeleteWithBody(
  url: string,
  body: any,
  customHeaders?: any,
  axiosInstance: AxiosInstance = defaultInstance(),
) {
  try {
    const response = await axiosInstance.delete(url, {
      headers: customHeaders,
      data: body,
    })
    return response.data
  } catch (error) {
    return handleError(error)
  }
}

export async function requestDeleteBatchWithBody(
  url: string,
  body: any,
  customHeaders?: any,
  axiosInstance: AxiosInstance = defaultInstance(),
) {
  try {
    const response = await axiosInstance.delete(url, {
      headers: customHeaders,
      data: body,
    })
    return {
      locationUrl: response.headers[Constants.LocationUrl] || undefined,
      retry: response.headers[Constants.RetryAfter] || undefined,
    }
  } catch (error) {
    return handleError(error)
  }
}

function handleError<T>(
  error: Error | AxiosError | any,
  fallback: boolean = false,
) {
  const fetchError = new FetchError(
    `An error occurred while performing the request.`,
    {
      cause: error,
    },
  )

  if (isAxiosError(error)) {
    const { request, response, config } = error
    fetchError.correlationId =
      config?.headers[Constants.HttpHeaderCorrelationId]

    if (request) {
      fetchError.info = request
    }

    if (response) {
      if (response.status === HttpStatusCode.NotFound && fallback) {
        return {} as T
      }
      if (response.status === HttpStatusCode.Locked) {
        fetchError.status = response.status
        fetchError.info = response.data || 'The resource is locked.'
        throw fetchError
      }
      const handleableError = getHandleableError(response.data)
      if (handleableError.title) fetchError.message = handleableError.title
      fetchError.info = handleableError.message
      fetchError.status = response.status
    }
  }

  throw fetchError
}
